import { ProductMeasurementJob, ProductType, ProposalProduct } from "generated/graphql";

const CUBIC_INCHES_IN_CUBIC_YARD = 46656;

const calculateMaterialNeededByTonConversion = (
  product: ProposalProduct,
  tonConversionFactor: number
): number => {
  let jobSize = product.jobSize;
  if (!jobSize || !tonConversionFactor) {
    return 0;
  }

  if (product.material.measurementJob === ProductMeasurementJob.SQUARE_YARD) {
    jobSize *= 9;
  }

  return tonConversionFactor * jobSize;
};

const calculateAsphaltCurbingMaterialNeeded = (
  product: ProposalProduct,
  tonConversionFactor: number,
  applicationRate?: number
): number => {
  const jobSize = product.jobSize;
  if (!jobSize || !tonConversionFactor) {
    return 0;
  }

  const attributes = product.material.attributes as any;
  const { curbWidth, curbHeight } = attributes;
  if (!curbWidth || !curbHeight) return 0;

  const appRate = applicationRate || (attributes?.applicationRate as number) || 1;

  const cubicFeet = (curbHeight / 12) * (curbWidth / 12) * jobSize;

  const weight = tonConversionFactor * 2000;
  const cubicInchesAtDepth = 144 * appRate;
  const cubicFeetAtDepth = cubicInchesAtDepth / 1728;
  const weightFactor = cubicFeetAtDepth > 0 ? weight / cubicFeetAtDepth : 0;

  const pounds = cubicFeet * weightFactor;
  const tons = pounds / 2000;

  return parseFloat(tons.toFixed(2));
};

const calculateConcreteColumnMaterialNeeded = (product: ProposalProduct): number => {
  const attributes = product.material.attributes as any;
  const height = attributes?.height;
  const diameter = attributes?.diameter;

  if (!height || !diameter) {
    return 0;
  }

  // radius in feet
  const radiusInFeet = diameter / 12 / 2;

  // volume in cubic feet
  const volumeInCubicFeet = Math.PI * Math.pow(radiusInFeet, 2) * height;

  // material needed in cubic yards
  const materialNeeded = volumeInCubicFeet / 27;

  if (product.jobSize > 0) {
    return materialNeeded * product.jobSize;
  }

  return materialNeeded;
};

const calculateConcreteCurbOrGutterMaterialNeeded = (product: ProposalProduct): number => {
  let jobSize = product.jobSize;
  if (!jobSize) {
    return 0;
  }

  const areaCoverage = 46656;

  if (product.material.measurementJob === ProductMeasurementJob.SQUARE_YARD) {
    jobSize *= 9;
  }

  const attributes = product.material.attributes as any;
  const { curbWidth, curbHeight, gutterWidth, gutterHeight } = attributes;
  if (!curbWidth || !curbHeight || !gutterWidth || !gutterHeight) return 0;

  const result =
    12 * curbWidth * curbHeight + 12 * gutterWidth * gutterHeight + 12 * gutterHeight * curbWidth;

  const cubicFeetOfConcrete = result === 0 ? 0 : areaCoverage / result;

  if (cubicFeetOfConcrete === 0) {
    return 0;
  }

  const materialNeeded = jobSize / cubicFeetOfConcrete;
  return parseFloat(materialNeeded.toFixed(10));
};

const calculateConcreteFlatworkMaterialNeeded = (
  product: ProposalProduct,
  applicationRate: number
): number => {
  let jobSize = product.jobSize;
  if (!jobSize) {
    return 0;
  }

  if (!applicationRate || applicationRate === 0) {
    return 0;
  }
  const areaCoverage = CUBIC_INCHES_IN_CUBIC_YARD / (144 * applicationRate);

  if (areaCoverage === 0) {
    return 0;
  }

  if (product.material.measurementJob === ProductMeasurementJob.SQUARE_YARD) {
    jobSize *= 9;
  }

  const materialNeeded = jobSize / areaCoverage;
  return parseFloat(materialNeeded.toFixed(10));
};

const calculateGradingMaterialNeeded = (
  product: ProposalProduct,
  applicationRate: number
): number => {
  let jobSize = product.jobSize;
  if (!jobSize) {
    return 0;
  }

  const attributes = product.material.attributes as any;
  const poundsPerCubicYard = attributes?.poundsPerCubicYard;

  if (!poundsPerCubicYard || !applicationRate) {
    return 0;
  }

  const cubicYardsPerTon = 2000 / poundsPerCubicYard;
  const cubicFeetPerTon = cubicYardsPerTon * 27;

  let areaCoverage = 0;
  if (product.material.measurementJob === ProductMeasurementJob.SQUARE_YARD) {
    const cubicYardsPerSquareYard = ((applicationRate / 12) * 9) / 27;
    if (cubicYardsPerSquareYard > 0) {
      areaCoverage = cubicYardsPerTon / cubicYardsPerSquareYard;
    }
  } else if (product.material.measurementJob === ProductMeasurementJob.SQUARE_FOOT) {
    const cubicFeetPerSquareFoot = (144 * applicationRate) / 1728;
    if (cubicFeetPerSquareFoot > 0) {
      areaCoverage = cubicFeetPerTon / cubicFeetPerSquareFoot;
    }
  }

  if (areaCoverage === 0) {
    return 0;
  }

  const materialNeeded = jobSize / areaCoverage;
  return parseFloat(materialNeeded.toFixed(10));
};

const calculateMicrosurfacingMaterialNeeded = (
  product: ProposalProduct,
  applicationRate: number
): number => {
  let jobSize = product.jobSize;
  if (!jobSize) {
    return 0;
  }

  if (!applicationRate) {
    return 0;
  }

  const areaCoverageFactor = applicationRate / 2000;

  if (product.material.measurementJob === ProductMeasurementJob.SQUARE_YARD) {
    jobSize *= 9;
  }

  const materialNeeded = jobSize * areaCoverageFactor;
  return parseFloat(materialNeeded.toFixed(10));
};

const calculateConcreteFootingMaterialNeeded = (product: ProposalProduct): number => {
  const attributes = product.material.attributes as any;
  const width = attributes?.width;
  const depth = attributes?.depth;
  const jobSize = product.jobSize;

  if (!width || !depth || !jobSize) {
    return 0;
  }

  const volumeInCubicFeet = (width / 12) * (depth / 12) * jobSize;
  const materialNeededInCubicYards = volumeInCubicFeet / 27;

  return parseFloat(materialNeededInCubicYards.toFixed(2));
};

const calculateConcreteWallMaterialNeeded = (product: ProposalProduct): number => {
  const attributes = product.material.attributes as any;
  const width = attributes?.width;
  const height = attributes?.height;
  const jobSize = product.jobSize;

  if (!width || !height || !jobSize) {
    return 0;
  }

  const volumeInCubicFeet = (width / 12) * height * jobSize;
  const materialNeededInCubicYards = volumeInCubicFeet / 27;

  return parseFloat(materialNeededInCubicYards.toFixed(2));
};

const calculateSlurrySealMaterialNeeded = (
  product: ProposalProduct,
  applicationRateInput: number
): number => {
  let jobSize = product.jobSize;
  if (!jobSize) {
    return 0;
  }

  let applicationRate = applicationRateInput;

  if (!applicationRate) {
    return 0;
  }

  if (product.material.measurementJob === ProductMeasurementJob.SQUARE_YARD) {
    jobSize *= 9;
    applicationRate /= 9;
  }

  const materialNeeded = (jobSize * applicationRate) / 2000;

  return parseFloat(materialNeeded.toFixed(2));
};

export const materialNeededCalculations = {
  [ProductType.ASPHALT]: calculateMaterialNeededByTonConversion,
  [ProductType.ASPHALT_CURBING]: calculateAsphaltCurbingMaterialNeeded,
  [ProductType.EXCAVATE_OUT]: calculateMaterialNeededByTonConversion,
  [ProductType.CONCRETE_COLUMN]: calculateConcreteColumnMaterialNeeded,
  [ProductType.CONCRETE_CURB_OR_GUTTER]: calculateConcreteCurbOrGutterMaterialNeeded,
  [ProductType.CONCRETE_FLATWORK]: calculateConcreteFlatworkMaterialNeeded,
  [ProductType.MICROSURFACING]: calculateMicrosurfacingMaterialNeeded,
  [ProductType.GRADING]: calculateGradingMaterialNeeded,
  [ProductType.CONCRETE_FOOTING]: calculateConcreteFootingMaterialNeeded,
  [ProductType.CONCRETE_WALL]: calculateConcreteWallMaterialNeeded,
  [ProductType.CONCRETE_WATERWAY]: calculateConcreteFlatworkMaterialNeeded,
  [ProductType.SLURRY_SEAL]: calculateSlurrySealMaterialNeeded,
};
